import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AiResultCell from '../../../custom/aiResultCell/aiResultCell';
import ArrowIcon from '../../../../assets/icons/chevron.svg';
import SearchIcon from '../../../../assets/icons/search.svg';
import {
	IAiReviewCriteriaItem,
	normalizeAiResult,
	shouldShowAiResults,
} from '../../../../utils/aiResults';
import { formatReportData } from '../../../../utils/formatReportData';
import { IUserOptions } from '../../../../types/interfaces';

const mapCriteriaResultLabel = (raw?: string | null): string => {
	if (!raw) {
		return '';
	}
	const key = raw.trim().toLowerCase();
	const labels: Record<string, string> = {
		done: 'Выполнено',
		completed: 'Выполнено',
		success: 'Выполнено',
		passed: 'Выполнено',
		pass: 'Выполнено',
		failed: 'Не выполнено',
		fail: 'Не выполнено',
		rejected: 'Не выполнено',
		declined: 'Не выполнено',
		unconfirmed: 'Не подтверждено',
		uncertain: 'Не проверялось',
		unchecked: 'Не проверялось',
		'не определено': 'Не проверялось',
	};
	return labels[key] || raw;
};

const AiCriteriaTableSection: FC = () => {
	const { aiReviewSummary } = useSelector(
		(state: any) => state.userSingleReport.userSingleReport
	);
	const { claims, isCustomer, settings: userSettings } = useSelector(
		(state: IUserOptions) => state.user.user.data
	);
	const showAiResults = shouldShowAiResults(
		isCustomer,
		userSettings?.aiReview,
		claims
	);
	const [isVisibleData, setIsVisibleData] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');

	const criteria: IAiReviewCriteriaItem[] = useMemo(
		() => aiReviewSummary?.criteria || [],
		[aiReviewSummary?.criteria]
	);

	const filteredCriteria = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		if (!query) {
			return criteria;
		}
		return criteria.filter((elem) => {
			const resultLabel = mapCriteriaResultLabel(elem.result).toLowerCase();
			const haystack = [elem.name, elem.result, resultLabel, elem.reason]
				.filter(Boolean)
				.join(' ')
				.toLowerCase();
			return haystack.includes(query);
		});
	}, [criteria, searchQuery]);

	if (isCustomer || !showAiResults) {
		return null;
	}

	if (!criteria.length && !aiReviewSummary?.criteriaMessage) {
		return null;
	}

	return (
		<div
			className={`sectionData ${
				isVisibleData ? 'sectionData_active sectionData__overflow' : ''
			}`}
		>
			<span className='section__title'>Проверка ИИ</span>
			<button
				className='sectionData_button'
				onClick={() => setIsVisibleData(!isVisibleData)}
			>
				<img src={ArrowIcon} alt='Custom Indicator' className='icon' />
			</button>

			{aiReviewSummary?.criteriaMessage && (
				<p
					style={{
						margin: '0 0 8px',
						fontSize: 13,
						color: '#2684ff',
					}}
				>
					{aiReviewSummary.criteriaMessage}
				</p>
			)}

			{!!criteria.length && (
				<>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 12,
							marginBottom: 8,
							flexWrap: 'wrap',
						}}
					>
						<label
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 8,
								minWidth: 200,
								flex: '1 1 220px',
								maxWidth: 360,
							}}
						>
							<img
								src={SearchIcon}
								alt=''
								width={16}
								height={16}
								style={{ opacity: 0.65, flexShrink: 0 }}
							/>
							<input
								type='search'
								value={searchQuery}
								onChange={(event) =>
									setSearchQuery(event.target.value)
								}
								placeholder='Найти в таблице...'
								style={{
									width: '100%',
									border: 'none',
									outline: 'none',
									background: 'transparent',
									fontSize: 14,
									color: 'var(--text-color, #333)',
								}}
							/>
						</label>
						<span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
							Строк: {filteredCriteria.length}
						</span>
					</div>
					<table className='customerOutlet__data_table'>
						<thead className='tunder__thead'>
							<tr>
								<th>Критерий</th>
								<th>Результат</th>
								<th>Причина</th>
							</tr>
						</thead>
						<tbody>
							{filteredCriteria.map((elem: any, index) => {
								const normalized = normalizeAiResult({
									result: elem.result,
									reason: elem.reason,
								});
								const resultLabel = mapCriteriaResultLabel(
									normalized?.result || elem.result
								);
								const isChanged = !!formatReportData(
									elem?.changes
								)?.length;
								const isPrevious = !!formatReportData(
									elem?.equals
								)?.length;

								return (
									<tr key={`${elem.name}-${index}`}>
										<td>
											<span
												className={
													isChanged
														? 'changed__table_value'
														: isPrevious
															? 'previous__table_value'
															: undefined
												}
											>
												{elem.name}
											</span>
										</td>
										<td className='ai-result-cell'>
											<AiResultCell
												item={
													resultLabel
														? { result: resultLabel }
														: null
												}
											/>
										</td>
										<td>
											{normalized?.comment ||
												elem.reason ||
												'—'}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					{!filteredCriteria.length && (
						<p style={{ margin: '8px 0', fontSize: 13 }}>
							Ничего не найдено
						</p>
					)}
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 16,
							marginTop: 10,
							fontSize: 13,
							alignItems: 'center',
						}}
					>
						<span
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
							}}
						>
							<span
								style={{
									width: 14,
									height: 14,
									background: '#e53935',
									borderRadius: 2,
									display: 'inline-block',
								}}
							/>
							Изменено после сдачи
						</span>
						<span
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
							}}
						>
							<span
								style={{
									width: 14,
									height: 14,
									background: '#f8f233',
									borderRadius: 2,
									display: 'inline-block',
								}}
							/>
							Совпадает с предыдущим отчётом
						</span>
					</div>
				</>
			)}
		</div>
	);
};

export default AiCriteriaTableSection;
